// src/frontend/js/modern/core/storage/idb-driver.js
//
// Thin adapter over the official `idb` library (https://github.com/jakearchibald/idb).
// Exposes exactly the small surface consumed by resilient-storage.js:
//   openDB(name, {version, upgrade}) -> promise-wrapped db handle with
//   get/getAll/getAllKeys/put/delete/clear/close — identical to `idb`'s own
//   database object, so consumers need zero changes.
//
// Requires: npm i idb@^8
import { openDB as idbOpen } from 'idb';

const DB_NAME = 'wlfmc_storage';
const DB_VERSION = 2;

export const KV_STORE = 'kv';

/**
 * Feature detection only — real viability is proven by openDB()+probe.
 */
export function isIndexedDBSupported() {
	try {
		return typeof window !== 'undefined' && !!window.indexedDB;
	} catch (e) {
		return false;
	}
}

/**
 * Open (and upgrade) the database using the `idb` library.
 * Handles blocked/terminated states gracefully so manual DB deletion
 * or stale tabs never permanently break the storage layer.
 *
 * @param {string} [dbName]
 * @param {{version?:number, upgrade?:Function, blocked?:Function, terminated?:Function}} [options]
 * @returns {Promise<object>} idb database handle.
 */
export function openDB(dbName = DB_NAME, options = {}) {
	return idbOpen(dbName, options.version || DB_VERSION, {
		upgrade(database, oldVersion, newVersion, transaction) {
			if (!database.objectStoreNames.contains(KV_STORE)) {
				database.createObjectStore(KV_STORE);
			}
			if (typeof options.upgrade === 'function') {
				options.upgrade(database, oldVersion, newVersion, transaction);
			}
		},
		blocked() {
			// Another tab still holds an old-version connection.
			// Log for diagnostics; the browser will eventually allow us through
			// once the other tab closes or navigates away.
			console.warn('[idb-driver] Database open blocked by another tab/connection.');
			if (typeof options.blocked === 'function') options.blocked();
		},
		terminated() {
			// Connection lost unexpectedly (browser killed it or user deleted DB).
			console.warn('[idb-driver] Database connection terminated unexpectedly.');
			if (typeof options.terminated === 'function') options.terminated();
		},
	});
}

/**
 * Force-delete the database (used for recovery from corrupted state).
 * Returns a promise that resolves when deletion is complete.
 */
export function deleteDB(dbName = DB_NAME) {
	return new Promise((resolve, reject) => {
		const request = indexedDB.deleteDatabase(dbName);
		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		request.onblocked = () => {
			console.warn('[idb-driver] Database deletion blocked by another tab.');
			// Still resolve; the browser will delete it once tabs close.
			resolve();
		};
	});
}
